import { useEffect, useState } from 'react';
// Honest sale signals on cards and the product page (API_CONTRACT §2j).
// UAE Federal Law 15/2020: every number comes from the API (`stock`,
// `sold_7d`, `sale_ends_at`, `badge`, the real prices); no data = no signal.

/** `sold_7d` at or above which "Selling fast" shows (the server omits less). */
export const SELLING_FAST_MIN = 5;
const MINUTE = 60000;
const HOUR = 3600000;
const DAY = 86400000;

/** ms until the product's real sale ends, or 0 (no end / already ended). */
export const saleEndsIn = (product, now = Date.now()) => {
  const end = Date.parse(product?.sale_ends_at || '');
  return Number.isFinite(end) && end > now ? end - now : 0;
};

/** {d, h, m, s} of a duration in ms. */
export const splitMs = (ms) => {
  const t = Math.max(0, Math.floor(ms / 1000));
  return { d: Math.floor(t / 86400), h: Math.floor((t % 86400) / 3600), m: Math.floor((t % 3600) / 60), s: t % 60 };
};

/** Card text "Ends in 2d" / "5h" / "12m", only under 7 days (else null). */
export const endsInShort = (ms) => {
  if (!ms || ms >= 7 * DAY) return null;
  const { d, h, m } = splitMs(ms);
  return d ? { en: `Ends in ${d}d`, ar: `ينتهي خلال ${d} يوم` }
    : h ? { en: `Ends in ${h}h`, ar: `ينتهي خلال ${h} ساعة` }
    : { en: `Ends in ${Math.max(1, m)}m`, ar: `ينتهي خلال ${Math.max(1, m)} دقيقة` };
};

export const soldThisWeek = (product) => {
  const n = Number(product?.sold_7d);
  return Number.isInteger(n) && n >= SELLING_FAST_MIN ? n : 0;
};

export const BADGE_TEXT = {
  exclusive: { en: 'Exclusive', ar: 'حصري' },
  limited: { en: 'Limited edition', ar: 'إصدار محدود' },
};

/**
 * Card badges, strongest first, at most 2 (the discount ticket is separate):
 * Sold out › Only N left › Selling fast › Exclusive / Limited edition › New.
 * Returns keys: 'out' | 'low' | 'fast' | 'exclusive' | 'limited' | 'new'.
 */
export const cardBadges = (product, lowStockAt) => {
  const stock = Number(product?.stock ?? 0);
  const out = !(stock > 0);
  return [
    out && 'out',
    !out && stock <= lowStockAt && 'low',
    !out && soldThisWeek(product) && 'fast',
    BADGE_TEXT[product?.badge] && product.badge,
    !out && product?.is_new && 'new',
  ].filter(Boolean).slice(0, 2);
};

/** Delay until a card's sale text or price next changes: the end itself, or
 * the next step of the compact "Ends in" label (minutes under an hour,
 * 5 minutes under a day, hourly otherwise). 0 = nothing to schedule. */
export const nextSaleTick = (product, now = Date.now()) => {
  const left = saleEndsIn(product, now);
  if (!left) return 0;
  const step = left < HOUR ? MINUTE : left < DAY ? 5 * MINUTE : HOUR;
  return Math.min(left + 50, step);
};

/** Re-renders the caller when the sale ends or its compact label changes,
 * so a card left open never keeps a sale the server has stopped. */
export const useSaleClock = (product) => {
  const [, setTick] = useState(0);
  const endsAt = product?.sale_ends_at;
  useEffect(() => {
    if (!endsAt) return undefined;
    let timer;
    const arm = () => {
      const delay = nextSaleTick({ sale_ends_at: endsAt });
      if (!delay) return;
      timer = setTimeout(() => { setTick((n) => n + 1); arm(); }, delay);
    };
    arm();
    return () => clearTimeout(timer);
  }, [endsAt]);
};
